import React, { useEffect, useRef, useCallback, useState } from 'react';
import { Terminal } from 'xterm';
import { FitAddon } from 'xterm-addon-fit';
import 'xterm/css/xterm.css';

type TerminalPanelProps = {
  onCommand?: (command: string) => void;
};

export const TerminalPanel = React.memo(function TerminalPanel({ onCommand }: TerminalPanelProps) {
  const terminalRef = useRef<HTMLDivElement>(null);
  const xtermRef = useRef<Terminal | null>(null);
  const fitAddonRef = useRef<FitAddon | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const commandBuffer = useRef('');
  const historyRef = useRef<string[]>([]);
  const historyIndexRef = useRef(-1);

  const writePrompt = useCallback(() => {
    if (xtermRef.current) {
      xtermRef.current.write('\r\n\x1b[36m❯\x1b[0m ');
    }
  }, []);

  const executeCommand = useCallback(
    (command: string) => {
      const term = xtermRef.current;
      if (!term) return;

      const trimmedCmd = command.trim();
      if (!trimmedCmd) {
        writePrompt();
        return;
      }

      // Add to history
      historyRef.current.push(trimmedCmd);
      historyIndexRef.current = historyRef.current.length;

      // Built-in commands
      if (trimmedCmd === 'clear' || trimmedCmd === 'cls') {
        term.clear();
        writePrompt();
        return;
      }

      if (trimmedCmd === 'help') {
        term.writeln('\r\n\x1b[33mAvailable commands:\x1b[0m');
        term.writeln('  \x1b[36mclear\x1b[0m     - Clear terminal');
        term.writeln('  \x1b[36mhelp\x1b[0m      - Show this help');
        term.writeln('  \x1b[36mecho\x1b[0m      - Print message');
        term.writeln('  \x1b[36mdate\x1b[0m      - Show current date');
        term.writeln('  \x1b[36mversion\x1b[0m   - Show IDE version');
        term.writeln('  \x1b[36mnpm\x1b[0m       - Simulate npm commands');
        writePrompt();
        return;
      }

      if (trimmedCmd.startsWith('echo ')) {
        term.writeln('\r\n' + trimmedCmd.slice(5));
        writePrompt();
        return;
      }

      if (trimmedCmd === 'date') {
        term.writeln('\r\n' + new Date().toLocaleString());
        writePrompt();
        return;
      }

      if (trimmedCmd === 'version') {
        term.writeln('\r\n\x1b[36mLodos Atlas IDE\x1b[0m v0.1.0');
        term.writeln('React 19.2.0 | TypeScript 4.9.5');
        writePrompt();
        return;
      }

      if (trimmedCmd.startsWith('npm ')) {
        const npmCmd = trimmedCmd.slice(4);
        term.writeln('');

        if (npmCmd === 'install' || npmCmd === 'i') {
          term.writeln('\x1b[32m⠋\x1b[0m Installing dependencies...');
          setTimeout(() => {
            term.writeln('\x1b[32m✓\x1b[0m Dependencies installed successfully');
            term.writeln('\x1b[90madded 42 packages in 2.1s\x1b[0m');
            writePrompt();
          }, 1000);
          return;
        }

        if (npmCmd === 'run build') {
          term.writeln('\x1b[36m>\x1b[0m react-ide@0.1.0 build');
          term.writeln('\x1b[36m>\x1b[0m react-scripts build');
          setTimeout(() => {
            term.writeln('\x1b[32m✓\x1b[0m Build completed successfully');
            writePrompt();
          }, 1500);
          return;
        }

        if (npmCmd === 'test') {
          term.writeln('\x1b[36m>\x1b[0m react-ide@0.1.0 test');
          term.writeln('\x1b[36m>\x1b[0m react-scripts test');
          setTimeout(() => {
            term.writeln('\x1b[32mPASS\x1b[0m src/App.test.tsx');
            term.writeln('\x1b[90mTests: 1 passed, 1 total\x1b[0m');
            writePrompt();
          }, 800);
          return;
        }

        if (npmCmd === 'start') {
          term.writeln('\x1b[36m>\x1b[0m react-ide@0.1.0 start');
          term.writeln('\x1b[36m>\x1b[0m react-scripts start');
          setTimeout(() => {
            term.writeln('\x1b[32m✓\x1b[0m Compiled successfully!');
            term.writeln('\x1b[90mLocal: http://localhost:3000\x1b[0m');
            writePrompt();
          }, 1200);
          return;
        }
      }

      // Unknown command
      term.writeln(`\r\n\x1b[31mCommand not found:\x1b[0m ${trimmedCmd}`);
      term.writeln('\x1b[90mType "help" for available commands\x1b[0m');
      writePrompt();

      // Notify parent if needed
      if (onCommand) {
        onCommand(trimmedCmd);
      }
    },
    [onCommand, writePrompt]
  );

  useEffect(() => {
    if (!terminalRef.current || isInitialized) return;

    const term = new Terminal({
      cursorBlink: true,
      fontSize: 13,
      fontFamily: "'JetBrains Mono', 'SFMono-Regular', Consolas, monospace",
      theme: {
        background: '#0b1224',
        foreground: '#e9eef7',
        cursor: '#22d3ee',
        cursorAccent: '#0b1224',
        selectionBackground: 'rgba(34, 211, 238, 0.3)',
        black: '#1e293b',
        red: '#ef4444',
        green: '#22c55e',
        yellow: '#eab308',
        blue: '#3b82f6',
        magenta: '#a855f7',
        cyan: '#22d3ee',
        white: '#f1f5f9',
        brightBlack: '#64748b',
        brightRed: '#f87171',
        brightGreen: '#4ade80',
        brightYellow: '#facc15',
        brightBlue: '#60a5fa',
        brightMagenta: '#c084fc',
        brightCyan: '#67e8f9',
        brightWhite: '#ffffff',
      },
      allowProposedApi: true,
    });

    const fitAddon = new FitAddon();
    term.loadAddon(fitAddon);
    term.open(terminalRef.current);
    fitAddon.fit();

    xtermRef.current = term;
    fitAddonRef.current = fitAddon;

    // Welcome message
    term.writeln('\x1b[36m╔══════════════════════════════════════════╗\x1b[0m');
    term.writeln('\x1b[36m║\x1b[0m   \x1b[1mLodos Atlas IDE Terminal\x1b[0m              \x1b[36m║\x1b[0m');
    term.writeln('\x1b[36m║\x1b[0m   Type "help" for available commands     \x1b[36m║\x1b[0m');
    term.writeln('\x1b[36m╚══════════════════════════════════════════╝\x1b[0m');
    writePrompt();

    // Handle input
    term.onKey(({ key, domEvent }) => {
      const printable = !domEvent.altKey && !domEvent.ctrlKey && !domEvent.metaKey;

      if (domEvent.key === 'Enter') {
        executeCommand(commandBuffer.current);
        commandBuffer.current = '';
      } else if (domEvent.key === 'Backspace') {
        if (commandBuffer.current.length > 0) {
          commandBuffer.current = commandBuffer.current.slice(0, -1);
          term.write('\b \b');
        }
      } else if (domEvent.key === 'ArrowUp') {
        // History navigation
        if (historyRef.current.length > 0 && historyIndexRef.current > 0) {
          historyIndexRef.current--;
          const historyCmd = historyRef.current[historyIndexRef.current];
          // Clear current line
          term.write('\r\x1b[K\x1b[36m❯\x1b[0m ' + historyCmd);
          commandBuffer.current = historyCmd;
        }
      } else if (domEvent.key === 'ArrowDown') {
        if (historyIndexRef.current < historyRef.current.length - 1) {
          historyIndexRef.current++;
          const historyCmd = historyRef.current[historyIndexRef.current];
          term.write('\r\x1b[K\x1b[36m❯\x1b[0m ' + historyCmd);
          commandBuffer.current = historyCmd;
        } else if (historyIndexRef.current === historyRef.current.length - 1) {
          historyIndexRef.current++;
          term.write('\r\x1b[K\x1b[36m❯\x1b[0m ');
          commandBuffer.current = '';
        }
      } else if (printable) {
        commandBuffer.current += key;
        term.write(key);
      }
    });

    // Handle paste
    term.onData((data) => {
      // Only handle paste events (multi-character input)
      if (data.length > 1 && !data.includes('\r') && !data.includes('\n')) {
        commandBuffer.current += data;
        term.write(data);
      }
    });

    setIsInitialized(true);

    // Handle resize
    const handleResize = () => {
      if (fitAddonRef.current) {
        fitAddonRef.current.fit();
      }
    };
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      term.dispose();
    };
  }, [isInitialized, executeCommand, writePrompt]);

  // Refit on visibility change
  useEffect(() => {
    if (fitAddonRef.current) {
      setTimeout(() => fitAddonRef.current?.fit(), 100);
    }
  });

  return (
    <div className="terminal-container">
      <div className="terminal-header">
        <span className="terminal-title">Terminal</span>
        <div className="terminal-actions">
          <button
            className="terminal-btn"
            onClick={() => {
              if (xtermRef.current) {
                xtermRef.current.clear();
                writePrompt();
              }
            }}
            title="Clear Terminal"
          >
            Clear
          </button>
        </div>
      </div>
      <div ref={terminalRef} className="terminal-content" />
    </div>
  );
});
